import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import cn from 'classnames';

interface HoverPopoverProps
  extends Omit<React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>, 'children'> {
  trigger: React.ReactElement;
  children: React.ReactNode;
  closeDelay?: number;
}

export function HoverPopover({
  trigger,
  children,
  closeDelay = 150,
  className,
  side = 'bottom',
  align = 'end',
  sideOffset = 8,
  ...contentProps
}: HoverPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef<ReturnType<typeof setTimeout>>();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const lastPointerType = React.useRef('');

  const show = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), closeDelay);
  };
  const handleOpenChange = (next: boolean) => {
    clearTimeout(closeTimer.current);
    setOpen(next);
  };

  React.useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger
        asChild
        onPointerDown={e => {
          lastPointerType.current = e.pointerType;
        }}
        onPointerEnter={e => {
          if (e.pointerType !== 'touch') show();
        }}
        onPointerLeave={hide}
        onFocus={e => {
          if (e.currentTarget.matches(':focus-visible')) show();
        }}
        onBlur={hide}
        onClick={e => {
          if (e.detail > 0 && open && lastPointerType.current === 'mouse') e.preventDefault();
        }}
        onKeyDown={e => {
          if (e.key === 'Tab' && !e.shiftKey && open) {
            e.preventDefault();
            contentRef.current?.focus();
          }
        }}
      >
        {trigger}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          tabIndex={0}
          onOpenAutoFocus={e => e.preventDefault()}
          onPointerEnter={show}
          onPointerLeave={hide}
          onBlur={e => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) hide();
          }}
          onKeyDown={e => {
            if (e.key === 'Tab' && e.shiftKey) {
              e.preventDefault();
              handleOpenChange(false);
            }
          }}
          className={cn(
            'z-50 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
            className,
          )}
          {...contentProps}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
